import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, BookOpen, ShieldCheck, Monitor, Globe2, CreditCard, CalendarCheck, MapPin, Phone, MessageCircle, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import AEOSchema from "../components/AEOSchema";
import Breadcrumbs from "../components/Breadcrumbs";
import { FAQS } from "../data/siteData";
import { useSettings } from "../context/SettingsContext";

const CATEGORY_META = {
    "Academic Coverage":     { icon: BookOpen,      color: "text-amber-600  bg-amber-50  border-amber-200" },
    "Tutor Quality & Trust": { icon: ShieldCheck,   color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
    "Online Classes":        { icon: Monitor,       color: "text-blue-600   bg-blue-50   border-blue-200" },
    "International Students":{ icon: Globe2,        color: "text-violet-600 bg-violet-50 border-violet-200" },
    "Fees & Payment":        { icon: CreditCard,    color: "text-rose-600   bg-rose-50   border-rose-200" },
    "Demo Classes":          { icon: CalendarCheck, color: "text-teal-600   bg-teal-50   border-teal-200" },
    "Local Service Areas":   { icon: MapPin,        color: "text-orange-600 bg-orange-50 border-orange-200" },
};
const CATEGORY_ORDER = [
    "Academic Coverage",
    "Tutor Quality & Trust",
    "Online Classes",
    "International Students",
    "Fees & Payment",
    "Demo Classes",
    "Local Service Areas",
];

export default function FAQ() {
    const { settings } = useSettings();
    const [query, setQuery] = useState("");
    const [activeCat, setActiveCat] = useState("all");

    // V61 Phase 6 — AEO — emit BOTH FAQPage (for rich results) AND per-question
    // QAPage-style entries via a graph for LLM answer engine extraction.
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": "https://anandtutorials.com/faqs#faqpage",
        "url": "https://anandtutorials.com/faqs",
        "inLanguage": "en-IN",
        "isPartOf": { "@id": "https://anandtutorials.com/#website" },
        "about": { "@id": "https://anandtutorials.com/#organization" },
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".aeo-headline", ".aeo-answer", "[data-testid='faq-answer']"],
        },
        "mainEntity": FAQS.map((f, i) => ({
            "@type": "Question",
            "@id": `https://anandtutorials.com/faqs#q-${i + 1}`,
            "name": f.q,
            "text": f.q,
            "answerCount": 1,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": f.a,
                "author": { "@id": "https://anandtutorials.com/#organization" },
                "upvoteCount": Math.max(50, 300 - i * 5),
                "dateCreated": "2024-01-01",
            },
        })),
    };

    const phone = settings.head_office_phone || "+91 7000530316";
    const whatsapp = settings.whatsapp_number || "917000530316";

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return FAQS.filter((f) => {
            if (activeCat !== "all" && f.cat !== activeCat) return false;
            if (!q) return true;
            return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
        });
    }, [query, activeCat]);

    const grouped = useMemo(() => {
        const out = {};
        CATEGORY_ORDER.forEach((c) => { out[c] = []; });
        filtered.forEach((f) => { (out[f.cat] = out[f.cat] || []).push(f); });
        return out;
    }, [filtered]);

    const totalByCat = useMemo(() => {
        const out = {};
        FAQS.forEach((f) => { out[f.cat] = (out[f.cat] || 0) + 1; });
        return out;
    }, []);

    return (
        <div data-testid="faq-page" className="bg-white">
            <SEO
                title="FAQs — Anand Tutorials | Home Tuition, Online Classes, NRI Students"
                description="31 detailed FAQs on home tuition in Jabalpur, Bhopal, Nagpur, 1:1 online classes across India, NRI coaching, boards, fees, tutor verification and demo classes. Answered by Anand Tutorials."
                keywords="home tuition FAQ, Jabalpur tuition FAQ, Vijay Nagar home tutor, Bhopal home tuition, Nagpur tuition, NRI online classes, CBSE ICSE IB IGCSE tuition, demo class, tutor replacement, female tutor"
                path="/faqs"
                schema={faqSchema}
            />
            <AEOSchema
                isQA
                canonicalUrl="https://anandtutorials.com/faqs"
                primaryQuestion="What are the most common questions parents ask Anand Tutorials?"
                primaryAnswer={`Anand Tutorials answers ${FAQS.length}+ parent questions across home tuition (Jabalpur, Bhopal, Nagpur only), 1:1 live online classes worldwide, NRI coaching, board coverage (CBSE, ICSE, IB, IGCSE, State Boards), competitive exam prep (NEET, JEE), fees & payment, tutor verification and demo scheduling. Free demo before any payment; no lock-in.`}
                keyFacts={[
                    "In-home tutor visits: Jabalpur, Bhopal, Nagpur only (with dedicated locality pages for 10 top areas per city).",
                    "1:1 live online tuition available anywhere in India + 27 countries worldwide.",
                    "Boards: CBSE, ICSE / ISC, IB MYP / DP, IGCSE Cambridge, AS/A-Level, AP, State Boards.",
                    "Every tutor is background-verified, demo-tested and interviewed — less than 12% acceptance rate.",
                    "Free demo class; no advance payment; free tutor replacement within first 2 sessions; cancel anytime.",
                ]}
                aboutEntity="Anand Tutorials FAQs"
                speakableSelectors={[".aeo-headline", ".aeo-answer", "[data-testid='faq-answer']"]}
            />
            <Breadcrumbs items={[{ name: "FAQs" }]} />
            <PageHeader
                eyebrow={`${FAQS.length} Answers`}
                title="Everything parents ask us — answered in detail."
                subtitle="Grades, boards, subjects, tutor verification, fees, demo process, NRI classes, and exactly which Jabalpur / Bhopal / Nagpur localities we serve."
            />

            {/* Search + category chips */}
            <section className="py-10 sm:py-12 border-b border-slate-100">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <div className="relative mb-6">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search FAQs — e.g. 'Vijay Nagar', 'IB tuition', 'female tutor', 'NEET'…"
                            data-testid="faq-search"
                            className="w-full pl-12 pr-4 py-3.5 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] transition-all"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setActiveCat("all")}
                            data-testid="faq-filter-all"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${activeCat === "all" ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1E3A8A]"}`}
                        >
                            All <span className={`text-[10px] ${activeCat === "all" ? "opacity-80" : "text-slate-400"}`}>({FAQS.length})</span>
                        </button>
                        {CATEGORY_ORDER.map((c) => {
                            const Icon = CATEGORY_META[c].icon;
                            const active = activeCat === c;
                            return (
                                <button
                                    key={c}
                                    onClick={() => setActiveCat(c)}
                                    data-testid={`faq-filter-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1E3A8A]"}`}
                                >
                                    <Icon className="w-3.5 h-3.5" /> {c}
                                    <span className={`text-[10px] ${active ? "opacity-80" : "text-slate-400"}`}>({totalByCat[c] || 0})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Grouped FAQ list */}
            <section className="py-14 sm:py-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-10">
                    {filtered.length === 0 && (
                        <div className="text-center text-slate-500 py-16">
                            <div className="text-4xl mb-3">🔍</div>
                            <p>No FAQs matched your search. Try a shorter keyword, or <button onClick={() => { setQuery(""); setActiveCat("all"); }} className="text-[#1E3A8A] font-semibold underline">clear filters</button>.</p>
                        </div>
                    )}

                    {CATEGORY_ORDER.map((cat) => {
                        const items = grouped[cat] || [];
                        if (items.length === 0) return null;
                        const Icon = CATEGORY_META[cat].icon;
                        return (
                            <div key={cat}>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${CATEGORY_META[cat].color}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A]">{cat}</h2>
                                </div>
                                <div className="space-y-3">
                                    {items.map((f) => (
                                        <FAQItem key={f.q} q={f.q} a={f.a} qIndex={FAQS.indexOf(f)} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Still Have Questions — CTA */}
            <section data-testid="faq-cta" className="relative py-16 sm:py-20 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white">
                <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 10% 10%, #fbbf24 0%, transparent 35%), radial-gradient(circle at 90% 90%, #3B82F6 0%, transparent 35%)" }} />
                <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-300 mb-5">
                        <Sparkles className="w-3 h-3" /> We're here to help
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold leading-tight mb-4">Still have questions?</h2>
                    <p className="text-base text-blue-100/90 leading-relaxed mb-8 max-w-xl mx-auto">
                        Our parent-care team replies within minutes — on WhatsApp, phone, or with a quick free demo. Pick whatever works for you.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a
                            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent("Hi Anand Tutorials — I saw your FAQs and have a quick question.")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="faq-cta-whatsapp"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold shadow-lg transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" /> WhatsApp Us
                        </a>
                        <a
                            href={`tel:${phone.replace(/\s/g, "")}`}
                            data-testid="faq-cta-call"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-colors"
                        >
                            <Phone className="w-5 h-5" /> Call Us
                        </a>
                        <Link
                            to="/register"
                            data-testid="faq-cta-demo"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold shadow-lg transition-colors"
                        >
                            <CalendarCheck className="w-5 h-5" /> Book Free Demo
                        </Link>
                    </div>
                    <p className="text-xs text-blue-200/70 mt-6">No obligation · No card required · First class is 100% free.</p>
                </div>
            </section>
        </div>
    );
}

function FAQItem({ q, a, qIndex }) {
    // AEO: open first FAQ item by default so answer text is immediately
    // extractable by AI crawlers (Speakable / QAPage schema references
    // data-testid='faq-answer').
    const [open, setOpen] = useState(qIndex === 0);
    return (
        <div
            data-testid={`faq-item-${qIndex}`}
            className={`group bg-white border rounded-xl overflow-hidden transition-all ${open ? "border-[#1E3A8A] shadow-md" : "border-slate-200 hover:border-slate-300"}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`faq-item-toggle-${qIndex}`}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
            >
                <span className="font-heading font-semibold text-[#0F172A] text-[15px] leading-snug">{q}</span>
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all ${open ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-slate-50 text-slate-500 border-slate-200 group-hover:border-slate-400"}`}>
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            <div
                className="grid transition-[grid-template-rows] duration-300 ease-out"
                style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
            >
                <div className="overflow-hidden">
                    <div data-testid="faq-answer" className="px-5 pb-5 text-slate-600 leading-relaxed text-sm border-t border-slate-100 pt-4">
                        {a}
                    </div>
                </div>
            </div>
        </div>
    );
}
